import { useEffect, useRef, useState } from "react";
import { windStrength } from "../lib/wind";
import { advanceWeatherFade, WEATHER_FADE_MS, weatherFadePhase } from "../lib/weather-transition";
import type { LightningLevel, RainLevel, SnowflakeSize, SnowLevel, WindSpeed } from "../types";

interface Props {
  rain: RainLevel;
  lightning: LightningLevel;
  snow: SnowLevel;
  snowflakeSize: SnowflakeSize;
  windSpeed: WindSpeed;
  bounds: { left: number; top: number; width: number; height: number };
  weatherMask?: string;
  paused?: boolean;
}

const RAIN: Record<RainLevel, number> = { off: 0, light: 0.34, medium: 0.67, heavy: 1 };
const LIGHTNING: Record<LightningLevel, number> = { off: 0, distant: 1, storm: 2, severe: 3 };
const SNOW: Record<SnowLevel, { count: number; opacity: number; speed: number }> = {
  off: { count: 0, opacity: 0, speed: 0 },
  light: { count: 130, opacity: 0.60, speed: 0.72 },
  medium: { count: 320, opacity: 0.78, speed: 1 },
  heavy: { count: 3_200, opacity: 1.14, speed: 1.34 },
};
const SNOW_SIZE: Record<SnowflakeSize, { base: number; spread: number }> = {
  small: { base: 5.5, spread: 2.2 }, medium: { base: 9, spread: 3.2 }, large: { base: 15, spread: 5 }, mixed: { base: 10, spread: 11 },
};

const VERTEX_SHADER = `
attribute vec2 aPosition;
varying vec2 vUv;
void main() {
  vUv = aPosition * 0.5 + 0.5;
  gl_Position = vec4(aPosition, 0.0, 1.0);
}`;

const FRAGMENT_SHADER = `
precision mediump float;
varying vec2 vUv;
uniform vec2 uResolution;
uniform float uTime;
uniform float uRain;
uniform float uLightning;
uniform float uWind;
uniform float uRainFade;
uniform float uLightningFade;

float hash(vec2 p) {
  p = fract(p * vec2(127.1, 311.7));
  p += dot(p, p + 34.73);
  return fract(p.x * p.y);
}

float rainImpactLayer(vec2 uv, float columns, float speed, float seed) {
  float aspect = uResolution.x / max(uResolution.y, 1.0);
  vec2 p = uv * vec2(columns * aspect, columns);
  vec2 id = floor(p);
  vec2 cell = fract(p) - 0.5;
  float random = hash(id + seed);
  float phase = fract(uTime * speed + random * 7.13);
  float present = step(1.0 - uRain * 0.74, hash(id + seed * 2.31));
  float angle = hash(id + seed * 4.17) * 6.2831853;
  vec2 turbulence = vec2(cos(angle), sin(angle)) * 0.038;
  vec2 prevailing = normalize(vec2(0.88, 0.42)) * (0.026 + max(0.0, uWind - 1.0) * 0.014);
  vec2 drift = (turbulence + prevailing) * phase * uWind;
  vec2 center = vec2(hash(id + seed * 5.7), hash(id.yx + seed * 8.9)) - 0.5 + drift;
  float distanceToImpact = length(cell - center);
  float radius = 0.035 + phase * 0.28;
  float ring = 1.0 - smoothstep(0.018, 0.050, abs(distanceToImpact - radius));
  float impact = 1.0 - smoothstep(0.018, 0.075, distanceToImpact);
  float ringFade = (1.0 - phase) * (1.0 - phase);
  float impactFade = exp(-phase * 24.0);
  return (ring * ringFade * 0.72 + impact * impactFade) * present;
}

float lightningPulse(float seconds, float level) {
  if (level < 0.5) return 0.0;
  float interval = level < 1.5 ? 8.5 : (level < 2.5 ? 5.2 : 3.25);
  float cycle = floor(seconds / interval);
  float phase = mod(seconds, interval);
  float onset = 0.45 + hash(vec2(cycle, 19.7)) * (interval - 1.15);
  float age = phase - onset;
  float first = exp(-max(age, 0.0) * 17.0) * step(0.0, age);
  float doubleChance = step(0.46, hash(vec2(cycle, 71.2)));
  float secondAge = age - 0.19;
  float second = exp(-max(secondAge, 0.0) * 22.0) * step(0.0, secondAge) * doubleChance * 0.72;
  float strength = level < 1.5 ? 0.16 : (level < 2.5 ? 0.34 : 0.58);
  return min(1.0, (first + second) * strength);
}

void main() {
  float rain = 0.0;
  if (uRain > 0.01) {
    rain += rainImpactLayer(vUv, 18.0, 1.75, 1.3) * 0.42;
    rain += rainImpactLayer(vUv, 28.0, 2.45, 4.7) * 0.55;
    rain += rainImpactLayer(vUv, 42.0, 3.25, 9.1) * 0.68;
  }
  rain = clamp(rain * (0.36 + uRain * 0.74) * uRainFade, 0.0, 0.72);
  float flash = lightningPulse(uTime, uLightning) * uLightningFade;
  vec3 rainColor = vec3(0.70, 0.82, 0.91);
  vec3 flashColor = vec3(0.80, 0.88, 1.0);
  float alpha = clamp(rain + flash, 0.0, 0.78);
  vec3 color = mix(rainColor, flashColor, flash / max(alpha, 0.001));
  gl_FragColor = vec4(color * alpha, alpha);
}`;

const SNOW_VERTEX_SHADER = `
precision highp float;
attribute vec4 aSnowflake;
uniform float uTime;
uniform float uSpeed;
uniform float uPointBase;
uniform float uPointSpread;
uniform float uWind;
varying float vOpacity;
varying float vRotation;

float hash1(float value) {
  return fract(sin(value * 127.17 + 31.73) * 43758.5453);
}

void main() {
  float cycleTime = aSnowflake.z + uTime * (0.026 + aSnowflake.w * 0.038) * uSpeed;
  float life = fract(cycleTime);
  float cycle = floor(cycleTime);
  float angle = hash1(aSnowflake.w * 91.7 + cycle * 17.3) * 6.2831853;
  vec2 direction = vec2(cos(angle), sin(angle));
  float travel = life * (0.025 + hash1(aSnowflake.x * 71.9 + cycle) * 0.070);
  vec2 wander = vec2(
    sin(uTime * (0.55 + aSnowflake.w) + aSnowflake.x * 17.0),
    cos(uTime * (0.48 + aSnowflake.x) + aSnowflake.y * 19.0)
  ) * (0.004 + aSnowflake.w * 0.010);
  vec2 prevailing = normalize(vec2(0.88, 0.42)) * life * (0.018 + max(0.0, uWind - 1.0) * 0.014);
  vec2 position = fract(aSnowflake.xy + (direction * travel + wander) * uWind + prevailing * uWind);
  gl_Position = vec4(position * 2.0 - 1.0, 0.0, 1.0);
  float originalSize = max(2.0, uPointBase + (aSnowflake.w - 0.5) * uPointSpread);
  float remaining = pow(max(0.0, 1.0 - life), 1.35);
  gl_PointSize = max(0.1, originalSize * remaining);
  vOpacity = smoothstep(0.0, 0.08, life) * (1.0 - smoothstep(0.68, 1.0, life)) * (0.58 + aSnowflake.w * 0.42);
  vRotation = uTime * (0.16 + aSnowflake.w * 0.48) + angle;
}`;

const SNOW_FRAGMENT_SHADER = `
precision mediump float;
uniform sampler2D uSnowflakeTexture;
uniform float uSnowOpacity;
varying float vOpacity;
varying float vRotation;
void main() {
  vec2 point = gl_PointCoord - 0.5;
  float sine = sin(vRotation);
  float cosine = cos(vRotation);
  vec2 rotated = mat2(cosine, -sine, sine, cosine) * point + 0.5;
  vec4 sprite = texture2D(uSnowflakeTexture, rotated);
  float alpha = sprite.a * vOpacity * uSnowOpacity;
  if (alpha < 0.015) discard;
  gl_FragColor = vec4(vec3(0.84, 0.93, 1.0) * sprite.rgb, alpha);
}`;

function descartesSnowflakeTexture() {
  const canvas = document.createElement("canvas"); canvas.width = 128; canvas.height = 128; const context = canvas.getContext("2d")!;
  context.translate(64, 64); context.lineCap = "round"; context.lineJoin = "round"; context.strokeStyle = "white"; context.fillStyle = "white"; context.shadowColor = "#d9efff"; context.shadowBlur = 5;
  for (let arm = 0; arm < 6; arm += 1) {
    context.save(); context.rotate(arm * Math.PI / 3); context.lineWidth = 4.2; context.beginPath(); context.moveTo(0, 5); context.lineTo(0, -51); context.stroke();
    for (const distance of [22, 34, 45]) {
      const branch = distance === 45 ? 9 : distance === 34 ? 12 : 10; context.lineWidth = distance === 45 ? 2.2 : 2.8;
      context.beginPath(); context.moveTo(0, -distance); context.lineTo(-branch, -distance + branch * 0.72); context.moveTo(0, -distance); context.lineTo(branch, -distance + branch * 0.72); context.stroke();
    }
    context.beginPath(); context.arc(0, -51, 3.4, 0, Math.PI * 2); context.fill(); context.restore();
  }
  context.shadowBlur = 3; context.beginPath(); context.arc(0, 0, 8, 0, Math.PI * 2); context.fill();
  return canvas;
}

function compile(gl: WebGLRenderingContext, type: number, source: string) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error("Unable to create weather shader.");
  gl.shaderSource(shader, source); gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) ?? "Weather shader failed to compile.";
    gl.deleteShader(shader); throw new Error(message);
  }
  return shader;
}

export function WeatherEffects({ rain, lightning, snow, snowflakeSize, windSpeed, bounds, weatherMask, paused = false }: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const settingsRef = useRef({ rain, lightning, snow, snowflakeSize, windSpeed }); settingsRef.current = { rain, lightning, snow, snowflakeSize, windSpeed };
  const enabled = rain !== "off" || lightning !== "off" || snow !== "off";
  const snowEnabled = snow !== "off";
  const staticSnowEnabled = paused && snowEnabled;
  const [present, setPresent] = useState(enabled);
  const redrawRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    if (enabled) { setPresent(true); return; }
    if (paused) { setPresent(false); return; }
    const timeout = window.setTimeout(() => setPresent(false), WEATHER_FADE_MS + 250);
    return () => window.clearTimeout(timeout);
  }, [enabled, paused]);

  useEffect(() => {
    if (!present || !canvasRef.current) return;
    const canvas = canvasRef.current;
    const gl = canvas.getContext("webgl", { alpha: true, antialias: false, depth: false, stencil: false, powerPreference: "low-power" });
    if (!gl) { canvas.dataset.weatherRenderer = "unavailable"; return; }

    let vertex: WebGLShader; let fragment: WebGLShader;
    try {
      vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
      fragment = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    } catch (error) {
      console.warn("Animated weather is unavailable:", error);
      return;
    }
    const program = gl.createProgram(); const buffer = gl.createBuffer();
    if (!program || !buffer) { gl.deleteShader(vertex); gl.deleteShader(fragment); return; }
    gl.attachShader(program, vertex); gl.attachShader(program, fragment); gl.linkProgram(program);
    if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
      console.warn("Animated weather is unavailable:", gl.getProgramInfoLog(program));
      gl.deleteBuffer(buffer); gl.deleteProgram(program); gl.deleteShader(vertex); gl.deleteShader(fragment); return;
    }
    canvas.dataset.weatherRenderer = "webgl";

    const position = gl.getAttribLocation(program, "aPosition");
    const resolution = gl.getUniformLocation(program, "uResolution");
    const time = gl.getUniformLocation(program, "uTime");
    const rainAmount = gl.getUniformLocation(program, "uRain");
    const lightningLevel = gl.getUniformLocation(program, "uLightning");
    const rainWind = gl.getUniformLocation(program, "uWind");
    const rainFadeUniform = gl.getUniformLocation(program, "uRainFade");
    const lightningFadeUniform = gl.getUniformLocation(program, "uLightningFade");
    gl.bindBuffer(gl.ARRAY_BUFFER, buffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]), gl.STATIC_DRAW);
    gl.useProgram(program); gl.enableVertexAttribArray(position); gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.clearColor(0, 0, 0, 0);

    let snowProgram: WebGLProgram | null = null; let snowBuffer: WebGLBuffer | null = null; let snowTexture: WebGLTexture | null = null;
    let snowVertex: WebGLShader | null = null; let snowFragment: WebGLShader | null = null; let snowPosition = -1;
    let snowTime: WebGLUniformLocation | null = null; let snowSpeed: WebGLUniformLocation | null = null; let snowPointBase: WebGLUniformLocation | null = null; let snowPointSpread: WebGLUniformLocation | null = null; let snowWind: WebGLUniformLocation | null = null; let snowOpacity: WebGLUniformLocation | null = null; let snowSampler: WebGLUniformLocation | null = null;
    if (!paused || snowEnabled) {
      try {
        snowVertex = compile(gl, gl.VERTEX_SHADER, SNOW_VERTEX_SHADER); snowFragment = compile(gl, gl.FRAGMENT_SHADER, SNOW_FRAGMENT_SHADER); snowProgram = gl.createProgram(); snowBuffer = gl.createBuffer(); snowTexture = gl.createTexture();
        if (!snowProgram || !snowBuffer || !snowTexture) throw new Error("Unable to allocate snow point sprites.");
        gl.attachShader(snowProgram, snowVertex); gl.attachShader(snowProgram, snowFragment); gl.linkProgram(snowProgram);
        if (!gl.getProgramParameter(snowProgram, gl.LINK_STATUS)) throw new Error(gl.getProgramInfoLog(snowProgram) ?? "Snow point-sprite program failed to link.");
        snowPosition = gl.getAttribLocation(snowProgram, "aSnowflake"); snowTime = gl.getUniformLocation(snowProgram, "uTime"); snowSpeed = gl.getUniformLocation(snowProgram, "uSpeed"); snowPointBase = gl.getUniformLocation(snowProgram, "uPointBase"); snowPointSpread = gl.getUniformLocation(snowProgram, "uPointSpread"); snowWind = gl.getUniformLocation(snowProgram, "uWind"); snowOpacity = gl.getUniformLocation(snowProgram, "uSnowOpacity"); snowSampler = gl.getUniformLocation(snowProgram, "uSnowflakeTexture");
        let seed = 0x5a17c0de; const random = () => { seed = Math.imul(seed ^ seed >>> 15, 1 | seed); seed ^= seed + Math.imul(seed ^ seed >>> 7, 61 | seed); return ((seed ^ seed >>> 14) >>> 0) / 4294967296; };
        const particles = new Float32Array(SNOW.heavy.count * 4);
        for (let index = 0; index < SNOW.heavy.count; index += 1) { particles[index * 4] = random(); particles[index * 4 + 1] = random(); particles[index * 4 + 2] = random(); particles[index * 4 + 3] = random(); }
        gl.bindBuffer(gl.ARRAY_BUFFER, snowBuffer); gl.bufferData(gl.ARRAY_BUFFER, particles, gl.STATIC_DRAW);
        gl.bindTexture(gl.TEXTURE_2D, snowTexture); gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE); gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE); gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, descartesSnowflakeTexture());
      } catch (error) {
        console.warn("Animated snow is unavailable:", error); if (snowBuffer) gl.deleteBuffer(snowBuffer); if (snowTexture) gl.deleteTexture(snowTexture); if (snowProgram) gl.deleteProgram(snowProgram); if (snowVertex) gl.deleteShader(snowVertex); if (snowFragment) gl.deleteShader(snowFragment);
        snowProgram = null; snowBuffer = null; snowTexture = null; snowVertex = null; snowFragment = null;
      }
    }
    canvas.dataset.snowRenderer = snowProgram ? "point-sprites" : snowEnabled ? "unavailable" : "off";

    let renderedFrames = 0; let frame = 0; let frameTimer: number | undefined; let stopped = false; let transitionAt = performance.now();
    let rainFade = paused ? (rain === "off" ? 0 : 1) : 0; let lightningFade = paused ? (lightning === "off" ? 0 : 1) : 0; let snowFade = paused ? (snow === "off" ? 0 : 1) : 0;
    let lastRain: Exclude<RainLevel, "off"> = rain === "off" ? "light" : rain;
    let lastLightning: Exclude<LightningLevel, "off"> = lightning === "off" ? "distant" : lightning;
    let lastSnow: Exclude<SnowLevel, "off"> = snow === "off" ? "light" : snow;
    const schedule = () => {
      if (paused || stopped) return;
      frameTimer = window.setTimeout(() => { frameTimer = undefined; frame = requestAnimationFrame(render); }, 1000 / 20);
    };
    const render = (now: number) => {
      if (stopped) return;
      const elapsed = Math.min(100, Math.max(0, now - transitionAt)); transitionAt = now;
      // Weather is intentionally soft. Cap its backing store so an animated
      // 4K projector never allocates a second near-4K full-screen surface.
      const maxBufferSide = 1920;
      const ratio = Math.max(0.1, Math.min(window.devicePixelRatio || 1, 0.75, maxBufferSide / Math.max(canvas.clientWidth, canvas.clientHeight, 1)));
      const width = Math.max(1, Math.round(canvas.clientWidth * ratio));
      const height = Math.max(1, Math.round(canvas.clientHeight * ratio));
      if (canvas.width !== width || canvas.height !== height) { canvas.width = width; canvas.height = height; gl.viewport(0, 0, width, height); }
      gl.clear(gl.COLOR_BUFFER_BIT); gl.disable(gl.BLEND); gl.useProgram(program); gl.bindBuffer(gl.ARRAY_BUFFER, buffer); gl.enableVertexAttribArray(position); gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      const current = settingsRef.current;
      if (current.rain !== "off") lastRain = current.rain; if (current.lightning !== "off") lastLightning = current.lightning; if (current.snow !== "off") lastSnow = current.snow;
      rainFade = paused ? (current.rain === "off" ? 0 : 1) : advanceWeatherFade(rainFade, current.rain !== "off", elapsed);
      lightningFade = paused ? (current.lightning === "off" ? 0 : 1) : advanceWeatherFade(lightningFade, current.lightning !== "off", elapsed);
      snowFade = paused ? (current.snow === "off" ? 0 : 1) : advanceWeatherFade(snowFade, current.snow !== "off", elapsed);
      const seconds = paused ? 0 : (Date.now() / 1000) % 3600;
      gl.uniform2f(resolution, width, height); gl.uniform1f(time, seconds);
      gl.uniform1f(rainAmount, RAIN[lastRain]); gl.uniform1f(lightningLevel, LIGHTNING[lastLightning]); gl.uniform1f(rainFadeUniform, rainFade); gl.uniform1f(lightningFadeUniform, lightningFade);
      gl.uniform1f(rainWind, windStrength(current.windSpeed));
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      const snowSettings = SNOW[lastSnow]; const sizeSettings = SNOW_SIZE[current.snowflakeSize];
      if (snowFade > 0.001 && snowSettings.count && snowProgram && snowBuffer && snowTexture && snowPosition >= 0) {
        gl.enable(gl.BLEND); gl.blendFunc(gl.SRC_ALPHA, gl.ONE); gl.useProgram(snowProgram); gl.bindBuffer(gl.ARRAY_BUFFER, snowBuffer); gl.enableVertexAttribArray(snowPosition); gl.vertexAttribPointer(snowPosition, 4, gl.FLOAT, false, 0, 0);
        gl.uniform1f(snowTime, seconds); gl.uniform1f(snowSpeed, snowSettings.speed); gl.uniform1f(snowPointBase, sizeSettings.base * ratio); gl.uniform1f(snowPointSpread, sizeSettings.spread * ratio); gl.uniform1f(snowWind, windStrength(current.windSpeed)); gl.uniform1f(snowOpacity, snowSettings.opacity * snowFade); gl.activeTexture(gl.TEXTURE0); gl.bindTexture(gl.TEXTURE_2D, snowTexture); gl.uniform1i(snowSampler, 0); gl.drawArrays(gl.POINTS, 0, snowSettings.count); gl.disable(gl.BLEND);
      }
      canvas.dataset.rainFade = rainFade.toFixed(3); canvas.dataset.rainTransition = weatherFadePhase(rainFade, current.rain !== "off");
      canvas.dataset.lightningFade = lightningFade.toFixed(3); canvas.dataset.lightningTransition = weatherFadePhase(lightningFade, current.lightning !== "off");
      canvas.dataset.snowFade = snowFade.toFixed(3); canvas.dataset.snowTransition = weatherFadePhase(snowFade, current.snow !== "off");
      renderedFrames += 1; canvas.dataset.weatherFrameCount = String(renderedFrames); canvas.dataset.renderedFrame = "true";
      schedule();
    };
    if (paused) render(performance.now()); else schedule();
    redrawRef.current = () => render(performance.now() + 33);

    return () => {
      redrawRef.current = () => undefined; stopped = true; cancelAnimationFrame(frame); if (frameTimer !== undefined) clearTimeout(frameTimer);
      if (snowBuffer) gl.deleteBuffer(snowBuffer); if (snowTexture) gl.deleteTexture(snowTexture); if (snowProgram) gl.deleteProgram(snowProgram); if (snowVertex) gl.deleteShader(snowVertex); if (snowFragment) gl.deleteShader(snowFragment);
      gl.deleteBuffer(buffer); gl.deleteProgram(program); gl.deleteShader(vertex); gl.deleteShader(fragment);
    };
  }, [paused, present, staticSnowEnabled]);
  useEffect(() => { if (enabled && paused) redrawRef.current(); }, [bounds.height, bounds.width, enabled, lightning, paused, rain, snow, snowflakeSize, windSpeed]);

  if (!present) return null;
  return <canvas ref={canvasRef} className="weather-effects-canvas" style={{ ...bounds, maskImage: weatherMask, WebkitMaskImage: weatherMask, maskSize: "100% 100%", WebkitMaskSize: "100% 100%" }} data-rain={rain} data-rain-model="top-down-impacts" data-lightning={lightning} data-snow={snow} data-snow-density={snow === "heavy" ? "blizzard" : snow} data-snowflake-size={snowflakeSize} data-snow-model="descartes-point-sprites" data-snow-motion="omnidirectional-shrink" data-snowflake-count={SNOW[snow].count} data-weather-mask={weatherMask ? "active" : "none"} data-wind-speed={windSpeed} data-wind-strength={windStrength(windSpeed)} data-weather-fade-ms="900" data-weather-transition-mode="independent-fade" data-animated={paused ? "false" : "true"} aria-hidden="true" />;
}
